/**
 * =====================================================
 * Qawaem Global Theme Controller
 * One centralized file to manage ALL application theming
 * =====================================================
 * 
 * This replaces the old DashboardThemeContext
 * No React context needed - pure vanilla JS + CSS variables
 */

const THEME_STORAGE_KEY = 'qawaem-theme-mode';
const THEME_CHANGE_EVENT = 'qawaem-theme-change';

/**
 * Get current theme from localStorage or default to 'dark'
 */
export function getCurrentTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    const legacy = localStorage.getItem('dashboard-light-mode');
    if (legacy === 'true') {
      localStorage.setItem(THEME_STORAGE_KEY, 'light');
      return 'light';
    }
    if (legacy === 'false') {
      localStorage.setItem(THEME_STORAGE_KEY, 'dark');
      return 'dark';
    }
    return 'dark';
  } catch (error) {
    console.error('Error reading theme from localStorage:', error);
    return 'dark';
  }
}

/**
 * Check if light mode is currently active
 */
export function isLightMode() {
  return getCurrentTheme() === 'light';
}

/**
 * Apply theme to DOM
 * Sets data-theme attribute on <html> element
 */
export function applyTheme(theme) {
  const root = document.documentElement;
  
  // Set transitioning flag (prevents flash during change)
  root.setAttribute('data-theme-transitioning', 'true');
  
  // Apply theme
  root.setAttribute('data-theme', theme);
  
  // Remove transitioning flag after a brief delay
  setTimeout(() => {
    root.removeAttribute('data-theme-transitioning');
  }, 50);
  
  // Dispatch custom event for components that need to react
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, {
    detail: { theme, isLight: theme === 'light' }
  }));
  
  console.log(`🎨 Theme applied: ${theme.toUpperCase()} MODE`);
}

/**
 * Set theme and persist to localStorage
 */
export function setTheme(theme) {
  try {
    const validTheme = theme === 'light' ? 'light' : 'dark';
    localStorage.setItem(THEME_STORAGE_KEY, validTheme);
    applyTheme(validTheme);
    return validTheme;
  } catch (error) {
    console.error('Error saving theme to localStorage:', error);
    applyTheme(theme);
    return theme;
  }
}

/**
 * Toggle between light and dark mode
 */
export function toggleTheme() {
  const current = getCurrentTheme();
  const newTheme = current === 'dark' ? 'light' : 'dark';
  return setTheme(newTheme);
}

/**
 * Initialize theme on app startup
 * Call this once when your app loads
 */
export function initializeTheme() {
  const theme = getCurrentTheme();
  applyTheme(theme);
  console.log(`🚀 Qawaem Theme System initialized: ${theme.toUpperCase()} MODE`);
}

/**
 * Listen to theme changes
 * Returns unsubscribe function
 * 
 * Usage:
 * const unsubscribe = onThemeChange((theme, isLight) => {
 *   console.log('Theme changed to:', theme);
 * });
 * 
 * // Later, to stop listening:
 * unsubscribe();
 */
export function onThemeChange(callback) {
  const handler = (event) => {
    callback(event.detail.theme, event.detail.isLight);
  };
  
  window.addEventListener(THEME_CHANGE_EVENT, handler);
  
  // Return unsubscribe function
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, handler);
  };
}

/**
 * React hook for components that need theme state
 * Use this ONLY if component needs to re-render on theme change
 */
export function useTheme() {
  // For React components
  if (typeof window === 'undefined') {
    return { theme: 'dark', isLight: false, toggleTheme: () => {} };
  }
  
  const [theme, setThemeState] = React.useState(getCurrentTheme());
  
  React.useEffect(() => {
    const unsubscribe = onThemeChange((newTheme) => {
      setThemeState(newTheme);
    });
    
    return unsubscribe;
  }, []);
  
  return {
    theme,
    isLight: theme === 'light',
    isDark: theme === 'dark',
    toggleTheme: () => {
      const newTheme = toggleTheme();
      setThemeState(newTheme);
    },
    setTheme: (newTheme) => {
      setTheme(newTheme);
      setThemeState(newTheme);
    }
  };
}

// Export for backwards compatibility
export const isDashboardLightMode = isLightMode;
export const toggleDashboardTheme = toggleTheme;

export default {
  getCurrentTheme,
  isLightMode,
  applyTheme,
  setTheme,
  toggleTheme,
  initializeTheme,
  onThemeChange,
  useTheme,
  // Backwards compatibility
  isDashboardLightMode,
  toggleDashboardTheme
};
